import { friendlyError } from '../../../utils/userMessages';
import { companyMediaCacheKey, resolveCompanyId, getCompanyLogoUrl, getCompanyCoverUrl } from '../../../utils/companyIdentity';
import { brandPalette as palette, getReadableColor, getContrastText } from '../../../utils/brandPalette';
// src/components/companies/CompanySettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Slider,
    Stack,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Fade,
    Zoom,
    alpha,
    Popover,
    MenuList,
    Autocomplete,
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    Language as LanguageIcon,
    Public as PublicIcon,
    People as PeopleIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Upload as UploadIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Verified as VerifiedIcon,
    Shield as ShieldIcon,
    CalendarToday as CalendarIcon,
    Notifications as NotificationsIcon,
    Security as SecurityIcon,
    EventNote as MeetingIcon,
    VideoCall as VideoCallIcon,
    Analytics as DataIcon,
    Storefront as StorefrontIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    TrendingUp as TrendingUpIcon,
    Star as StarIcon,
    Schedule as TimeIcon,
    Description as DescriptionIcon,
    Link as LinkIcon,
    Flag as FlagIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Image as ImageIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Check as CheckIcon,
    AutoAwesome as AutoAwesomeIcon,
    Handshake as HandshakeIcon,
    PersonAdd as PersonAddIcon,
    Inventory2 as CategoryIcon,
    Pending as PendingIcon,
    PhotoCamera as PhotoCameraIcon,
    Business as DomainIcon,
    BadgeOutlined as WorkIcon,
    LocationOn as MapIcon,
    ImageOutlined as AddPhotoIcon,
    Group as GroupIcon,
    WorkspacePremium as SubscriptionIcon,
    Stars as StarsIcon,
    LinkedIn as LinkedInIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    WhatsApp as WhatsAppIcon,
    Message as TelegramIcon,
    ArrowForward as ArrowForwardIcon,
    FiberManualRecord as LiveIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import Header from '../../common/Header/Header';
import LoadingSpinner from '../../common/LoadingSpinner';
import { db } from '../../../config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import SubscriptionSettings from './SubscriptionSettings';
import CalendarIntegrationSettings from './CalendarIntegrationSettings';
import { uploadFile, deleteFile } from '../../../services/integrations/storage';
import { updateCompanyProfile as updateCompanyProfileBackend } from '../../../services/integrations/backend';

// ============================================
// DESIGN SYSTEM - MATCHES DASHBOARD
// ============================================



const safeAlpha = (color, opacity) => {
    if (!color) return `rgba(15,23,42, ${opacity})`;
    try {
        return alpha(color, opacity);
    } catch (e) {
        return `rgba(15,23,42, ${opacity})`;
    }
};

const shadows = {
    xs: '0 1px 2px rgba(25,57,78,0.04), 0 1px 4px rgba(25,57,78,0.04)',
    sm: '0 2px 4px rgba(25,57,78,0.06), 0 4px 12px rgba(25,57,78,0.08)',
    md: '0 4px 8px rgba(25,57,78,0.08), 0 8px 20px rgba(25,57,78,0.10)',
    lg: '0 8px 16px rgba(25,57,78,0.10), 0 16px 40px rgba(25,57,78,0.12)',
    xl: '0 16px 32px rgba(25,57,78,0.12), 0 24px 64px rgba(25,57,78,0.15)',
};

const glow = (color, intensity = 0.3) => {
    if (!color) return '0 4px 20px rgba(18,45,64,0.20)';
    try {
        return `0 4px 20px ${alpha(color, intensity)}, 0 8px 40px ${alpha(color, intensity * 0.5)}, 0 16px 60px ${alpha(color, intensity * 0.2)}`;
    } catch (e) {
        return `0 4px 20px rgba(18,45,64,0.20)`;
    }
};

const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500&family=Inter:wght@400;500;600;700;800;900&display=swap');
`;

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Inter', sans-serif";

// ============================================
// CONSTANTS
// ============================================

const companyTypes = [
    'Operator', 'Aggregator', 'Platform Provider', 'Infrastructure Provider',
    'Training Provider', 'HR Services', 'Marketing Agency', 'Consultant',
    'Reseller', 'Distributor'
];

const primaryServices = ['Voice', 'SMS', 'Platform', 'Wholesale', 'Testing', 'Training', 'IT Solutions', 'Data', 'VAS', 'Marketing', 'HR'];
const secondaryServices = primaryServices;
const primaryRegions = ['Europe', 'Africa', 'Asia', 'Middle East', 'USA & Canada', 'LATAM', 'Australia/Oceania'];
const secondaryRegions = primaryRegions;
const primaryConnectWith = ['Voice', 'SMS', 'Platform', 'Wholesale', 'Testing Providers', 'Platform Retail', 'Data', 'VAS', 'Other'];
const secondaryConnectWith = primaryConnectWith;

const companySizes = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const timezones = [
    { value: 'UTC-12:00', label: 'UTC-12:00 (Baker Island)' },
    { value: 'UTC-11:00', label: 'UTC-11:00 (American Samoa)' },
    { value: 'UTC-10:00', label: 'UTC-10:00 (Hawaii)' },
    { value: 'UTC-09:00', label: 'UTC-09:00 (Alaska)' },
    { value: 'UTC-08:00', label: 'UTC-08:00 (Pacific Time)' },
    { value: 'UTC-07:00', label: 'UTC-07:00 (Mountain Time)' },
    { value: 'UTC-06:00', label: 'UTC-06:00 (Central Time)' },
    { value: 'UTC-05:00', label: 'UTC-05:00 (Eastern Time)' },
    { value: 'UTC-04:00', label: 'UTC-04:00 (Atlantic Time)' },
    { value: 'UTC-03:00', label: 'UTC-03:00 (Brasilia)' },
    { value: 'UTC-02:00', label: 'UTC-02:00 (Mid-Atlantic)' },
    { value: 'UTC-01:00', label: 'UTC-01:00 (Azores)' },
    { value: 'UTC+00:00', label: 'UTC+00:00 (London)' },
    { value: 'UTC+01:00', label: 'UTC+01:00 (Paris)' },
    { value: 'UTC+02:00', label: 'UTC+02:00 (Athens)' },
    { value: 'UTC+03:00', label: 'UTC+03:00 (Moscow)' },
    { value: 'UTC+04:00', label: 'UTC+04:00 (Dubai)' },
    { value: 'UTC+05:00', label: 'UTC+05:00 (Karachi)' },
    { value: 'UTC+05:30', label: 'UTC+05:30 (India)' },
    { value: 'UTC+06:00', label: 'UTC+06:00 (Dhaka)' },
    { value: 'UTC+07:00', label: 'UTC+07:00 (Bangkok)' },
    { value: 'UTC+08:00', label: 'UTC+08:00 (Singapore)' },
    { value: 'UTC+09:00', label: 'UTC+09:00 (Tokyo)' },
    { value: 'UTC+10:00', label: 'UTC+10:00 (Sydney)' },
    { value: 'UTC+11:00', label: 'UTC+11:00 (Solomon Is.)' },
    { value: 'UTC+12:00', label: 'UTC+12:00 (Auckland)' }
];

const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ar', label: 'Arabic' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ru', label: 'Russian' },
    { value: 'de', label: 'German' }
];

const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
];

const meetingTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'video', label: 'Video Calls' },
    { value: 'in-person', label: 'In-Person' },
    { value: 'phone', label: 'Phone Calls' }
];

const meetingDurations = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '60 min' },
    { value: 90, label: '90 min' },
    { value: 120, label: '120 min' }
];

const countries = [
    { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
    { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
    { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
    { code: 'AE', name: 'UAE', dialCode: '+971', flag: '🇦🇪' },
    { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
    { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
    { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
    { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
    { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
    { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
    { code: 'MA', name: 'Morocco', dialCode: '+212', flag: '🇲🇦' },
    { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
    { code: 'TN', name: 'Tunisia', dialCode: '+216', flag: '🇹🇳' },
    { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
    { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
    { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
    { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
    { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
    { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
    { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
    { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
    { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
    { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
    { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
    { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
    { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
    { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
    { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
    { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
    { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
    { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
    { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
    { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
    { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
    { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
    { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
    { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
    { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
    { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪' },
    { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
    { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
    { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦' },
    { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
    { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
    { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
    { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
    { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
    { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
    { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
    { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
    { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
    { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
    { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
    { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
    { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
];

// ============================================
// PHONE COUNTRY SELECTOR POPOVER
// ============================================

const PhoneCountrySelector = ({ open, anchorEl, onClose, onSelect, selectedCountry, countries }) => {
    const [search, setSearch] = useState('');

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dialCode.includes(search)
    );

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            slotProps={{ paper: {
                sx: {
                    width: 320,
                    maxHeight: 400,
                    borderRadius: '16px',
                    border: `1px solid ${palette.hairline}`,
                    boxShadow: shadows.xl,
                    overflow: 'hidden',
                }
            } }}
        >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${palette.hairline}` }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            bgcolor: palette.canvasAlt,
                            '& input': { fontSize: '0.85rem', py: 0.75 }
                        }
                    }}
                    slotProps={{ input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16, color: palette.inkLight }} />
                            </InputAdornment>
                        ),
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    } }}
                />
            </Box>
            <MenuList sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
                {filteredCountries.map((country) => (
                    <MenuItem
                        key={country.code}
                        onClick={() => {
                            onSelect(country);
                            onClose();
                        }}
                        sx={{
                            py: 1,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: `1px solid ${palette.hairline}`,
                            '&:hover': {
                                bgcolor: safeAlpha(palette.rust, 0.06),
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: 20 }}>{country.flag}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: selectedCountry?.code === country.code ? 600 : 400 }}>
                            {country.name}
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.8rem' }}>
                            {country.dialCode}
                        </Typography>
                        {selectedCountry?.code === country.code && (
                            <CheckCircleIcon sx={{ color: getReadableColor(palette.rust), fontSize: 16 }} />
                        )}
                    </MenuItem>
                ))}
                {filteredCountries.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem' }}>
                            No countries found
                        </Typography>
                    </Box>
                )}
            </MenuList>
        </Popover>
    );
};

// ============================================
// ELEVATED CARD - MATCHES DASHBOARD
// ============================================

const ElevatedCard = ({ 
    children, 
    elevation = 'md', 
    color = palette.rust, 
    hover = true, 
    delay = 0,
    sx = {},
    ...props 
}) => {
    const shadow = shadows[elevation] || shadows.md;
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={hover ? { 
                y: -3,
                transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] }
            } : {}}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1.6, md: 1.8 },
                    borderRadius: '16px',
                    border: `1px solid ${safeAlpha(color, 0.13)}`,
                    background: `linear-gradient(135deg, ${palette.paper} 0%, ${safeAlpha(color, 0.03)} 100%)`,
                    boxShadow: isHovered && hover ? `0 18px 40px ${safeAlpha(color,.10)}, 0 5px 14px rgba(25,57,78,.055), inset 0 1px 0 rgba(255,255,255,.96)` : '0 12px 30px rgba(25,57,78,.078), 0 3px 9px rgba(25,57,78,.03), inset 0 1px 0 rgba(255,255,255,.95)',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    boxSizing: 'border-box',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${safeAlpha(color, 0.3)}, transparent)`,
                        opacity: isHovered ? 1 : 0.6,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '20px 20px 0 0',
                    },
                    '&::after': {
                        content: '""', position: 'absolute', right: -48, bottom: -58, width: 150, height: 150, borderRadius: '50%',
                        background: `radial-gradient(circle at 35% 35%, ${safeAlpha(color,.14)}, transparent 68%)`, pointerEvents: 'none',
                    },
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Paper>
        </motion.div>
    );
};

// ============================================
// SECTION HEADING - MATCHES DASHBOARD
// ============================================

const SectionHeading = ({ icon, title, chip, color = palette.rust, action, onAction }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon && (
                <Box sx={{
                    width: 34, height: 34, borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    color: '#fff', flexShrink: 0,
                    boxShadow: `0 4px 12px ${color}30`,
                }}>
                    {icon}
                </Box>
            )}
            <Box>
                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '.86rem', color: palette.ink }}>
                    {title}
                </Typography>
                <Box
                    sx={{
                        width: 30,
                        height: 2,
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${color}, transparent)`,
                        mt: 0.75,
                        animation: 'shimmerRule 3.2s ease-in-out infinite',
                    }}
                />
            </Box>
            {chip && (
                <Chip label={chip} size="small" sx={{ bgcolor: `${color}14`, color, fontWeight: 700, fontSize: '0.65rem', height: 22, ml: 0.5 }} />
            )}
        </Box>
        {action && (
            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onAction} sx={{ color: getReadableColor(palette.rust), fontWeight: 700, textTransform: 'none' }}>
                {action}
            </Button>
        )}
    </Box>
);

// ============================================
// ANIMATED BACKGROUND ORBS - MATCHES DASHBOARD
// ============================================

const AnimatedBackground = () => (
    <Box
        aria-hidden="true"
        sx={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
            background: `radial-gradient(circle at 8% 4%, rgba(var(--primary-rgb), .075), transparent 29rem), radial-gradient(circle at 92% 22%, rgba(var(--secondary-rgb), .055), transparent 30rem)`,
        }}
    />
);

const CompanySettings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userData, companyData, refreshCompanyData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const normalizedRole = String(userData?.role || userData?.userRole || '').toLowerCase();

    useEffect(() => {
        const requestedTab = new URLSearchParams(location.search).get('tab');
        if (requestedTab === 'subscription') setActiveTab(6);
        if (requestedTab === 'integrations') setActiveTab(0);
        if (requestedTab === 'calendar') setActiveTab(2);
    }, [location.search]);
    const [logoUrl, setLogoUrl] = useState(null);
    const [coverUrl, setCoverUrl] = useState(null);
    const [logoAsset, setLogoAsset] = useState(null);
    const [coverAsset, setCoverAsset] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    
    // Phone country selection
    const [countrySearchAnchorEl, setCountrySearchAnchorEl] = useState(null);
    const [phoneCountryAnchorEl, setPhoneCountryAnchorEl] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countrySearch, setCountrySearch] = useState('');

    const fileInputRef = useRef(null);
    const coverFileInputRef = useRef(null);

    const [settings, setSettings] = useState({
        companyName: '',
        companyType: '',
        primaryColor: '#2E6F8E',
        secondaryColor: '#42B89B',
        accentColor: '#7C5CF2',
        website: '',
        primaryService: '',
        secondaryService: '',
        primaryRegion: '',
        secondaryRegion: '',
        primaryConnectWith: '',
        secondaryConnectWith: '',
        otherConnectWith: '',
        description: '',
        country: null,
        dialCode: '+1',
        countryCode: 'US',
        phone: '',
        email: '',
        companySize: '',
        city: '',
        address: '',
        yearFounded: '',
        timezone: 'UTC+05:30',
        dateFormat: 'MM/DD/YYYY',
        language: 'en',
        workingHours: {
            start: '09:00',
            end: '18:00',
            days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
        },
        defaultMeetingDuration: 30,
        meetingTypes: ['video', 'in-person', 'phone'],
        autoAcceptMeetings: false,
        requireMeetingAgenda: true,
        allowRecording: true,
        allowChat: true,
        maxParticipants: 10,
        bufferTime: 15,
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: false,
        meetingReminders: true,
        meetingConfirmations: true,
        meetingCancellations: true,
        twoFactorAuth: false,
        sessionTimeout: 60,
        requireMeetingPassword: false,
        allowScreenSharing: true,
        allowPublicProfile: true,
        showInSearch: true,
        enableAnalytics: true,
        autoSyncCalendar: false,
        emailDomain: '',
        meetingRoomPrefix: '',
        linkedin: '',
        facebook: '',
        twitter: '',
        instagram: '',
        youtube: '',
        whatsapp: '',
        telegram: '',
    });

    const recoverCompanyId = async () => {
        const resolved = resolveCompanyId({ companyData, userData, currentUser });
        if (resolved) return resolved;
        if (!currentUser?.uid) return null;

        // Safe legacy-owner recovery: verify a company document actually belongs
        // to this authenticated user before treating the Firebase UID as a
        // company id. This avoids the old cross-tenant global fallback problem.
        try {
            const direct = await getDoc(doc(db, 'companies', currentUser.uid));
            if (direct.exists()) {
                const row = direct.data() || {};
                const ownerIds = [row.adminId, row.ownerId, row.createdBy, row.userId, row.uid].filter(Boolean).map(String);
                const ownerEmails = [row.adminEmail, row.ownerEmail].filter(Boolean).map((value) => String(value).toLowerCase());
                const email = String(currentUser.email || '').toLowerCase();
                const hasExplicitOwner = ownerIds.length > 0 || ownerEmails.length > 0;
                const explicitlyOwned = ownerIds.includes(String(currentUser.uid)) || (email && ownerEmails.includes(email));
                // Legacy company records were sometimes keyed directly by the owner's UID
                // without owner metadata. Accept that shape only when no contradictory
                // ownership fields exist; explicit owner metadata always wins.
                if (explicitlyOwned || !hasExplicitOwner) return direct.id;
                console.warn('Ignoring UID-keyed company document with mismatched owner metadata.');
            }
        } catch (error) {
            console.warn('Direct owner company lookup unavailable:', error?.message || error);
        }

        try {
            const checks = [
                getDocs(query(collection(db, 'companies'), where('adminId', '==', currentUser.uid))),
                getDocs(query(collection(db, 'companies'), where('ownerId', '==', currentUser.uid))),
                getDocs(query(collection(db, 'companies'), where('createdBy', '==', currentUser.uid))),
            ];
            if (currentUser.email) {
                checks.push(getDocs(query(collection(db, 'companies'), where('adminEmail', '==', currentUser.email))));
                checks.push(getDocs(query(collection(db, 'companies'), where('ownerEmail', '==', currentUser.email))));
            }
            const results = await Promise.allSettled(checks);
            for (const result of results) {
                if (result.status === 'fulfilled' && !result.value.empty) return result.value.docs[0].id;
            }
        } catch (error) {
            console.warn('Company owner lookup unavailable:', error?.message || error);
        }

        return null;
    };

    const ensureCompanyId = async () => {
        const resolved = companyId || await recoverCompanyId();
        if (resolved && resolved !== companyId) setCompanyId(resolved);
        return resolved || null;
    };

    useEffect(() => {
        let active = true;
        const bootstrap = async () => {
            setLoading(true);
            const cId = await recoverCompanyId();
            if (!active) return;
            setCompanyId(cId || null);
            if (cId) {
                await fetchSettings(cId);
            } else {
                setLoading(false);
            }
        };
        bootstrap();
        return () => { active = false; };
        // Auth/company objects are the authoritative triggers for this bootstrap.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyData, userData, currentUser?.uid, currentUser?.email]);

    // ============================================
    // FETCH SETTINGS
    // ============================================

    const fetchSettings = async (companyIdOverride = null) => {
        setLoading(true);
        try {
            const resolvedCompanyId = typeof companyIdOverride === 'string' ? companyIdOverride : companyId;
            if (!resolvedCompanyId) return;
            
            const companyRef = doc(db, 'companies', resolvedCompanyId);
            const companyDoc = await getDoc(companyRef);
            
            if (companyDoc.exists()) {
                const data = companyDoc.data();
                
                const country = countries.find(c => 
                    c.name === data.country || c.code === data.countryCode
                ) || null;

                setSelectedCountry(country);

                setSettings(prev => ({
                    ...prev,
                    companyName: data.companyName || '',
                    companyType: data.companyType || '',
                    primaryColor: data.primaryColor || data.branding?.primaryColor || '#2E6F8E',
                    secondaryColor: data.secondaryColor || data.branding?.secondaryColor || '#42B89B',
                    accentColor: data.accentColor || data.branding?.accentColor || '#7C5CF2',
                    website: data.website || '',
                    primaryService: data.primaryService || '',
                    secondaryService: data.secondaryService || '',
                    primaryRegion: data.primaryRegion || '',
                    secondaryRegion: data.secondaryRegion || '',
                    primaryConnectWith: data.primaryConnectWith || '',
                    secondaryConnectWith: data.secondaryConnectWith || '',
                    otherConnectWith: data.otherConnectWith || '',
                    description: data.description || '',
                    country: country,
                    dialCode: data.dialCode || '+1',
                    countryCode: data.countryCode || 'US',
                    phone: data.phone || '',
                    email: data.email || '',
                    companySize: data.companySize || '',
                    city: data.city || '',
                    address: data.address || '',
                    yearFounded: data.yearFounded || '',
                    timezone: data.timezone || 'UTC+05:30',
                    dateFormat: data.dateFormat || 'MM/DD/YYYY',
                    language: data.language || 'en',
                    workingHours: data.workingHours || {
                        start: '09:00',
                        end: '18:00',
                        days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                    },
                    defaultMeetingDuration: data.defaultMeetingDuration || 30,
                    meetingTypes: data.meetingTypes || ['video', 'in-person', 'phone'],
                    autoAcceptMeetings: data.autoAcceptMeetings || false,
                    requireMeetingAgenda: data.requireMeetingAgenda !== undefined ? data.requireMeetingAgenda : true,
                    allowRecording: data.allowRecording !== undefined ? data.allowRecording : true,
                    allowChat: data.allowChat !== undefined ? data.allowChat : true,
                    maxParticipants: data.maxParticipants || 10,
                    bufferTime: data.bufferTime || 15,
                    emailNotifications: data.emailNotifications !== undefined ? data.emailNotifications : true,
                    pushNotifications: data.pushNotifications !== undefined ? data.pushNotifications : true,
                    smsNotifications: data.smsNotifications || false,
                    meetingReminders: data.meetingReminders !== undefined ? data.meetingReminders : true,
                    meetingConfirmations: data.meetingConfirmations !== undefined ? data.meetingConfirmations : true,
                    meetingCancellations: data.meetingCancellations !== undefined ? data.meetingCancellations : true,
                    twoFactorAuth: data.twoFactorAuth || false,
                    sessionTimeout: data.sessionTimeout || 60,
                    requireMeetingPassword: data.requireMeetingPassword || false,
                    allowScreenSharing: data.allowScreenSharing !== undefined ? data.allowScreenSharing : true,
                    allowPublicProfile: data.allowPublicProfile !== undefined ? data.allowPublicProfile : true,
                    showInSearch: data.showInSearch !== undefined ? data.showInSearch : true,
                    enableAnalytics: data.enableAnalytics !== undefined ? data.enableAnalytics : true,
                    autoSyncCalendar: data.autoSyncCalendar || false,
                    emailDomain: data.emailDomain || '',
                    meetingRoomPrefix: data.meetingRoomPrefix || '',
                    linkedin: data.linkedin || '',
                    facebook: data.facebook || '',
                    twitter: data.twitter || '',
                    instagram: data.instagram || '',
                    youtube: data.youtube || '',
                    whatsapp: data.whatsapp || '',
                    telegram: data.telegram || '',
                }));
                
                setLogoUrl(getCompanyLogoUrl(data) || null);
                setCoverUrl(getCompanyCoverUrl(data) || null);
                setLogoAsset(data.logoAsset || null);
                setCoverAsset(data.coverAsset || null);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // HANDLE CHANGES
    // ============================================

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleNestedChange = (parent, field, value) => {
        setSettings(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [field]: value }
        }));
    };

    const handleWorkingDaysToggle = (day) => {
        const currentDays = [...settings.workingHours.days];
        const index = currentDays.indexOf(day);
        if (index > -1) {
            currentDays.splice(index, 1);
        } else {
            currentDays.push(day);
        }
        setSettings(prev => ({
            ...prev,
            workingHours: { ...prev.workingHours, days: currentDays }
        }));
    };

    const handleMeetingTypeToggle = (type) => {
        const currentTypes = [...settings.meetingTypes];
        const index = currentTypes.indexOf(type);
        if (index > -1) {
            currentTypes.splice(index, 1);
        } else {
            currentTypes.push(type);
        }
        setSettings(prev => ({ ...prev, meetingTypes: currentTypes }));
    };

    const handleSelectCountry = (country) => {
        setSelectedCountry(country);
        setSettings(prev => ({
            ...prev,
            country,
            dialCode: country.dialCode,
            countryCode: country.code,
        }));
    };

    const handleSelectPhoneCountry = (country) => {
        setSettings(prev => ({
            ...prev,
            dialCode: country.dialCode,
            countryCode: country.code,
        }));
        setPhoneCountryAnchorEl(null);
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setSettings(prev => ({ ...prev, phone: value }));
    };

    // ============================================
    // HANDLE LOGO UPLOAD
    // ============================================

    const handleLogoUpload = async (event) => {
        const activeCompanyId = await ensureCompanyId();
        if (!activeCompanyId) { toast.error('No company is linked to this account. Please refresh your profile or contact the administrator.'); return; }
        const file = event.target.files[0];
        if (!file) return;

        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            toast.error('Use a PNG, JPG or WEBP image. SVG files are not accepted for security reasons.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Logo image size should be less than 5MB');
            return;
        }

        setUploading(true);
        try {
            const asset = await uploadFile({ companyId: activeCompanyId, file, kind: 'image', folder: 'company-logos', publicId: `company_${activeCompanyId}_logo_${Date.now()}` });
            const downloadUrl = asset.url;
            
            await updateCompanyProfileBackend(activeCompanyId, {
                logoUrl: downloadUrl, logo: downloadUrl, companyLogo: downloadUrl, companyLogoUrl: downloadUrl, logoAsset: asset,
            });

            setLogoUrl(downloadUrl);
            setLogoAsset(asset);
            try {
                localStorage.setItem(companyMediaCacheKey(String(activeCompanyId), 'logo'), downloadUrl);
                window.dispatchEvent(new CustomEvent('telcofair-company-logo-updated', { detail: { companyId: activeCompanyId, logoUrl: downloadUrl } }));
            } catch (_) {}
            await refreshCompanyData?.(activeCompanyId);
            toast.success('Logo uploaded successfully!');
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(friendlyError(error, 'Failed to upload logo'));
        } finally {
            setUploading(false);
            if (event?.target) event.target.value = '';
        }
    };

    const handleRemoveLogo = async () => {
        const activeCompanyId = await ensureCompanyId();
        if (!activeCompanyId) { toast.error('No company is linked to this account.'); return; }
        if (!logoUrl) return;

        setUploading(true);
        try {
            if (logoAsset) {
                try { await deleteFile({ companyId: activeCompanyId, asset: logoAsset }); }
                catch (cleanupError) { console.warn('Remote logo cleanup skipped; removing TELCOFAIR reference only:', cleanupError); }
            }
            
            await updateCompanyProfileBackend(activeCompanyId, {
                logoUrl: null, logo: null, companyLogo: null, companyLogoUrl: null, logoAsset: null,
            });

            setLogoUrl(null);
            setLogoAsset(null);
            try {
                localStorage.removeItem(companyMediaCacheKey(String(activeCompanyId), 'logo'));
                window.dispatchEvent(new CustomEvent('telcofair-company-logo-updated', { detail: { companyId: activeCompanyId, logoUrl: '' } }));
            } catch (_) {}
            await refreshCompanyData?.(activeCompanyId);
            toast.success('Logo removed successfully!');
        } catch (error) {
            console.error('Error removing logo:', error);
            toast.error('Failed to remove logo');
        } finally {
            setUploading(false);
        }
    };

    const handleCoverUpload = async (event) => {
        const activeCompanyId = await ensureCompanyId();
        if (!activeCompanyId) { toast.error('No company is linked to this account.'); return; }
        const file = event.target.files[0];
        if (!file) return;

        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            toast.error('Use a PNG, JPG or WEBP image. SVG files are not accepted for security reasons.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }

        setUploading(true);
        try {
            const asset = await uploadFile({ companyId: activeCompanyId, file, kind: 'image', folder: 'company-covers', publicId: `company_${activeCompanyId}_cover_${Date.now()}` });
            const downloadUrl = asset.url;
            
            await updateCompanyProfileBackend(activeCompanyId, {
                coverImage: downloadUrl, coverImageUrl: downloadUrl, companyCover: downloadUrl, companyCoverUrl: downloadUrl, coverAsset: asset,
            });

            setCoverUrl(downloadUrl);
            setCoverAsset(asset);
            try {
                localStorage.setItem(companyMediaCacheKey(String(activeCompanyId), 'cover'), downloadUrl);
                window.dispatchEvent(new CustomEvent('telcofair-company-cover-updated', { detail: { companyId: activeCompanyId, coverUrl: downloadUrl } }));
            } catch (_) {}
            await refreshCompanyData?.(activeCompanyId);
            toast.success('Cover image uploaded successfully!');
        } catch (error) {
            console.error('Error uploading cover:', error);
            toast.error(friendlyError(error, 'Failed to upload cover image'));
        } finally {
            setUploading(false);
            if (event?.target) event.target.value = '';
        }
    };

    const handleRemoveCover = async () => {
        const activeCompanyId = await ensureCompanyId();
        if (!activeCompanyId) { toast.error('No company is linked to this account.'); return; }
        if (!coverUrl) return;

        setUploading(true);
        try {
            if (coverAsset) {
                try { await deleteFile({ companyId: activeCompanyId, asset: coverAsset }); }
                catch (cleanupError) { console.warn('Remote cover cleanup skipped; removing TELCOFAIR reference only:', cleanupError); }
            }
            
            await updateCompanyProfileBackend(activeCompanyId, {
                coverImage: null, coverImageUrl: null, companyCover: null, companyCoverUrl: null, coverAsset: null,
            });

            setCoverUrl(null);
            setCoverAsset(null);
            try {
                localStorage.removeItem(companyMediaCacheKey(String(activeCompanyId), 'cover'));
                window.dispatchEvent(new CustomEvent('telcofair-company-cover-updated', { detail: { companyId: activeCompanyId, coverUrl: '' } }));
            } catch (_) {}
            await refreshCompanyData?.(activeCompanyId);
            toast.success('Cover image removed successfully!');
        } catch (error) {
            console.error('Error removing cover:', error);
            toast.error('Failed to remove cover image');
        } finally {
            setUploading(false);
        }
    };

    // ============================================
    // SAVE SETTINGS
    // ============================================

    const handleSave = async () => {
        const activeCompanyId = await ensureCompanyId();
        if (!activeCompanyId) {
            toast.error('No company is linked to this account.');
            return;
        }
        setSaving(true);
        try {
            // Company identity/profile settings save directly to Firestore. Global visual theme
            // is platform-owned by Super Admin and is intentionally not written here.
            // These company settings must not depend on
            // Cloud Functions deployment or external integration availability.
            const companyPatch = {
                companyName: settings.companyName,
                name: settings.companyName,
                companyType: settings.companyType,
                website: settings.website,
                primaryService: settings.primaryService,
                secondaryService: settings.secondaryService,
                primaryRegion: settings.primaryRegion,
                secondaryRegion: settings.secondaryRegion,
                primaryConnectWith: settings.primaryConnectWith,
                secondaryConnectWith: settings.secondaryConnectWith,
                otherConnectWith: settings.otherConnectWith,
                description: settings.description,
                country: settings.country?.name || '',
                countryCode: settings.country?.code || settings.countryCode,
                dialCode: settings.dialCode,
                phone: settings.phone,
                email: settings.email,
                companySize: settings.companySize,
                city: settings.city,
                address: settings.address,
                yearFounded: settings.yearFounded,
                emailDomain: settings.emailDomain,
                linkedin: settings.linkedin,
                facebook: settings.facebook,
                twitter: settings.twitter,
                instagram: settings.instagram,
                youtube: settings.youtube,
                whatsapp: settings.whatsapp,
                telegram: settings.telegram,
                logo: logoUrl || null,
                logoUrl: logoUrl || null,
                companyLogo: logoUrl || null,
                companyLogoUrl: logoUrl || null,
                coverImage: coverUrl || null,
                coverImageUrl: coverUrl || null,
                companyCover: coverUrl || null,
                companyCoverUrl: coverUrl || null,
                branding: {
                                logo: logoUrl || null,
                    logoUrl: logoUrl || null,
                    coverImage: coverUrl || null,
                    coverImageUrl: coverUrl || null,
                    companyName: settings.companyName,
                },
                timezone: settings.timezone,
                dateFormat: settings.dateFormat,
                language: settings.language,
                workingHours: settings.workingHours,
                defaultMeetingDuration: settings.defaultMeetingDuration,
                meetingTypes: settings.meetingTypes,
                autoAcceptMeetings: settings.autoAcceptMeetings,
                requireMeetingAgenda: settings.requireMeetingAgenda,
                allowRecording: settings.allowRecording,
                allowChat: settings.allowChat,
                maxParticipants: settings.maxParticipants,
                bufferTime: settings.bufferTime,
                emailNotifications: settings.emailNotifications,
                pushNotifications: settings.pushNotifications,
                smsNotifications: settings.smsNotifications,
                meetingReminders: settings.meetingReminders,
                meetingConfirmations: settings.meetingConfirmations,
                meetingCancellations: settings.meetingCancellations,
                allowPublicProfile: settings.allowPublicProfile,
                showInSearch: settings.showInSearch,
                enableAnalytics: settings.enableAnalytics,
                autoSyncCalendar: settings.autoSyncCalendar,
                meetingRoomPrefix: settings.meetingRoomPrefix,
            };

            await updateCompanyProfileBackend(activeCompanyId, companyPatch);

            if (typeof window !== 'undefined') {
                if (settings.companyName) window.localStorage.setItem(companyMediaCacheKey(String(activeCompanyId), 'name'), settings.companyName);
                if (logoUrl) window.localStorage.setItem(companyMediaCacheKey(String(activeCompanyId), 'logo'), logoUrl);
            }

            window.dispatchEvent(new CustomEvent('telcofair-company-branding-updated', {
                detail: {
                    companyId: activeCompanyId,
                    companyName: settings.companyName,
                    logoUrl: logoUrl || settings.logo || null,
                    coverUrl: coverUrl || null,
                }
            }));
            try {
                await refreshCompanyData?.(activeCompanyId);
            } catch (refreshError) {
                console.warn('Company data refresh failed after a successful direct save:', refreshError);
            }
            toast.success('Company profile saved successfully!');
        } catch (error) {
            console.error('Error saving settings:', error);
            const message = friendlyError(error, 'Failed to save settings');
            if (String(message).toLowerCase().includes('corporate email')) {
                toast.error(message);
            } else {
                toast.error(message);
            }
        } finally {
            setSaving(false);
        }
    };

    // ============================================
    // CANVAS STYLES - MATCHES DASHBOARD
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: fontBody,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.025%27/%3E%3C/svg%3E")',
            animation: 'grainDrift 9s steps(6) infinite',
        },
    };

    // ============================================
    // TEXT FIELD STYLES - FIXED SIZING
    // ============================================

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: palette.canvasAlt,
            border: `1px solid ${palette.hairline}`,
            transition: 'all 0.3s ease',
            minHeight: '56px',
            '&:hover': {
                borderColor: palette.rust,
                backgroundColor: palette.paper,
                boxShadow: `0 4px 12px ${safeAlpha(palette.rust, 0.15)}`,
            },
            '&.Mui-focused': {
                borderColor: palette.rust,
                backgroundColor: palette.paper,
                boxShadow: `0 4px 16px ${safeAlpha(palette.rust, 0.20)}`,
            },
            '& input, & textarea': {
                fontSize: '0.9rem',
                fontWeight: 500,
                color: palette.ink,
                padding: '16px 14px',
            },
            '& textarea': {
                padding: '14px',
            }
        },
        '& .MuiInputLabel-root': {
            color: palette.inkSoft,
            fontWeight: 600,
            fontSize: '0.85rem',
            '&.Mui-focused': {
                color: getReadableColor(palette.rust),
            }
        },
        width: '100%',
    };

    const selectFieldSx = {
        borderRadius: '12px',
        backgroundColor: palette.canvasAlt,
        border: `1px solid ${palette.hairline}`,
        transition: 'all 0.3s ease',
        minHeight: '56px',
        width: '100%',
        '&:hover': {
            borderColor: palette.rust,
            backgroundColor: palette.paper,
            boxShadow: `0 4px 12px ${safeAlpha(palette.rust, 0.15)}`,
        },
        '&.Mui-focused': {
            borderColor: palette.rust,
            backgroundColor: palette.paper,
            boxShadow: `0 4px 16px ${safeAlpha(palette.rust, 0.20)}`,
        },
        '& .MuiSelect-select': {
            fontSize: '0.9rem',
            fontWeight: 500,
            color: palette.ink,
            padding: '16px 14px !important',
            minHeight: '24px',
        },
        '& .MuiSelect-icon': {
            color: palette.inkLight,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.hairline,
        }
    };

    // WIDENED SELECT FIELD STYLES FOR DROPDOWNS
    const widenedSelectFieldSx = {
        ...selectFieldSx,
        '& .MuiSelect-select': {
            ...selectFieldSx['& .MuiSelect-select'],
            padding: '18px 16px !important',
            fontSize: '0.95rem',
        },
        '& .MuiMenuItem-root': {
            padding: '12px 20px',
            fontSize: '0.95rem',
        }
    };

    const formControlSx = {
        width: '100%',
        '& .MuiInputLabel-root': {
            color: palette.inkSoft,
            fontWeight: 600,
            fontSize: '0.85rem',
            '&.Mui-focused': {
                color: getReadableColor(palette.rust),
            }
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
        }
    };

    // ============================================
    // RENDER
    // ============================================

    if (loading) {
        return <LoadingSpinner label="Loading company settings…" />;
    }

    return (
        <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
            <style>{fontImport}</style>
            <Header />

            <Container maxWidth={false} sx={{ maxWidth: 1580, py: { xs: 2, md: 2.5 }, px: { xs: 1.5, sm: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}>
                {/* Header - Matches Dashboard */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box className="erp-page-hero" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2.5, mb: 2.5 }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: { xs: '1.45rem', md: '1.7rem' },
                                    color: palette.ink,
                                    letterSpacing: '-0.01em',
                                    lineHeight: 1.2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <SettingsIcon sx={{ fontSize: 28, color: getReadableColor(palette.rust) }} />
                                Company Settings
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.95rem' }}>
                                Manage company identity, operations, notifications and subscription preferences
                            </Typography>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 3,
                                    borderRadius: 2,
                                    background: `linear-gradient(90deg, ${palette.rust}, transparent)`,
                                    mt: 0.75,
                                    animation: 'shimmerRule 3.2s ease-in-out infinite',
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Chip
                                icon={<LiveIcon sx={{ fontSize: 10, color: `${palette.pine} !important` }} />}
                                label="Live"
                                sx={{
                                    bgcolor: 'rgba(45,90,71,0.08)',
                                    color: getReadableColor(palette.pine),
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 26,
                                    borderRadius: '16px',
                                    border: '1px solid rgba(45,90,71,0.12)',
                                }}
                            />
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ display: activeTab >= 6 ? 'none' : 'block' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchSettings}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        borderColor: palette.hairline,
                                        color: palette.ink,
                                        '&:hover': { borderColor: palette.rust, bgcolor: `${safeAlpha(palette.rust, 0.08)}` }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </motion.div>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ display: activeTab >= 6 ? 'none' : 'block' }}>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        py: 0.8,
                                        px: 2.5,
                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                        boxShadow: '0 6px 16px -8px rgba(199,91,46,0.45)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px -8px rgba(199,91,46,0.55)',
                                            transform: 'translateY(-2px)',
                                        },
                                        '&:disabled': {
                                            background: palette.inkLight,
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </motion.div>
                        </Box>
                    </Box>
                </motion.div>

                {/* Settings workspace navigator — persistent left rail on desktop, compact grid on smaller screens */}
                <Box className="tf-settings-workspace-grid">
                <Paper elevation={0} className="tf-settings-section-navigator">
                    <Box className="tf-settings-section-navigator-head">
                        <Box>
                            <Typography className="tf-command-eyebrow">SETTINGS WORKSPACE</Typography>
                            <Typography sx={{ mt: .25, fontSize: '1rem', fontWeight: 950, color: palette.ink, letterSpacing: '-.025em' }}>Choose what you want to configure</Typography>
                            <Typography sx={{ mt: .2, fontSize: '.66rem', color: palette.inkLight }}>Manage your company profile, meetings, calendar availability, notifications, privacy and subscription. Platform integrations are managed centrally by TELCOFAIR administration.</Typography>
                        </Box>
                    </Box>
                    <Box className="tf-settings-section-nav-scroll">
                        {[
                            { title: 'Company', helper: 'Brand, profile & theme', icon: <BusinessIcon />, color: '#2F80ED', tab: 0 },
                            { title: 'Meetings', helper: 'Rooms & participant rules', icon: <MeetingIcon />, color: '#7C5CF2', tab: 1 },
                            { title: 'Calendar', helper: 'Google, Outlook & ICS', icon: <CalendarIcon />, color: '#2F6FED', tab: 2 },
                            { title: 'Notifications', helper: 'Email, push & SMS', icon: <NotificationsIcon />, color: '#F39A24', tab: 3 },
                            { title: 'Security', helper: 'Access & privacy', icon: <SecurityIcon />, color: '#E84B7A', tab: 4 },
                            { title: 'Advanced', helper: 'Operational defaults', icon: <DataIcon />, color: '#0F9E9A', tab: 5 },
                            { title: 'Subscription', helper: 'Plan, seats & billing', icon: <SubscriptionIcon />, color: '#13A773', tab: 6 },
                        ].map((item) => (
                            <Button
                                key={item.title}
                                onClick={() => setActiveTab(item.tab)}
                                className={`tf-settings-section-nav-item ${activeTab === item.tab ? 'is-active' : ''}`}
                                sx={{ '--setting-color': item.color }}
                                startIcon={<Box className="tf-settings-nav-icon">{item.icon}</Box>}
                                endIcon={<ArrowForwardIcon className="tf-settings-nav-arrow" />}
                            >
                                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                                    <Typography className="tf-settings-nav-title">{item.title}</Typography>
                                    <Typography className="tf-settings-nav-helper" noWrap>{item.helper}</Typography>
                                </Box>
                            </Button>
                        ))}
                    </Box>
                </Paper>

                {/* Main Settings Paper */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <ElevatedCard color={palette.rust} delay={0.1} hover={false}>
                        <Box className="tf-settings-content-heading">
                            <Box className="tf-settings-content-heading-icon">
                                {[<BusinessIcon />, <MeetingIcon />, <CalendarIcon />, <NotificationsIcon />, <SecurityIcon />, <DataIcon />, <SubscriptionIcon />][activeTab]}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography className="tf-command-eyebrow">ACTIVE SETTINGS SECTION</Typography>
                                <Typography sx={{ mt: .15, fontSize: '.98rem', fontWeight: 950, color: palette.ink }}>
                                    {['Company & Branding','Meeting Experience','Calendar Integration','Notifications','Security & Privacy','Preferences','Subscription & Billing'][activeTab]}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>
                            {/* TAB 0: COMPANY SETTINGS */}
                            {activeTab === 0 && (
                                <Fade in={activeTab === 0} timeout={500}>
                                    <Box>
                                        {/* Logo & Cover Section */}
                                        <ElevatedCard color={palette.gold} delay={0.1} hover={false}>
                                            <SectionHeading 
                                                icon={<StorefrontIcon sx={{ fontSize: 19 }} />} 
                                                title="Company Branding" 
                                                chip="logo & cover"
                                                color={palette.gold}
                                            />
                                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                        <Box
                                                            onClick={() => fileInputRef.current?.click()}
                                                            sx={{
                                                                width: 100,
                                                                height: 100,
                                                                borderRadius: '14px',
                                                                border: `2px dashed ${logoUrl ? palette.rust : palette.hairline}`,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: logoUrl ? '#fff' : palette.canvasAlt,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                boxShadow: logoUrl ? shadows.sm : 'none',
                                                                '&:hover': {
                                                                    borderColor: palette.rust,
                                                                    boxShadow: `0 8px 24px ${safeAlpha(palette.rust, 0.15)}`,
                                                                }
                                                            }}
                                                        >
                                                            {logoUrl && <img src={logoUrl} alt="Company logo preview" style={{ width:'100%', height:'100%', objectFit:'contain', padding:8, display:'block' }} />}
                                                            {!logoUrl && (
                                                                <>
                                                                    <AddPhotoIcon sx={{ color: getReadableColor(palette.rust), fontSize: 28, mb: 0.5 }} />
                                                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.65rem', fontWeight: 700 }}>LOGO</Typography>
                                                                </>
                                                            )}
                                                            <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
                                                        </Box>
                                                    </motion.div>
                                                    {logoUrl && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={handleRemoveLogo}
                                                            sx={{ 
                                                                textTransform: 'none', 
                                                                fontWeight: 600, 
                                                                fontSize: '0.75rem',
                                                                color: palette.danger,
                                                                '&:hover': { bgcolor: safeAlpha(palette.danger, 0.08) }
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                    <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, fontWeight: 500 }}>
                                                        Logo (PNG/JPG/WEBP · 5MB max)
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                                        <Box
                                                            onClick={() => coverFileInputRef.current?.click()}
                                                            sx={{
                                                                width: { xs: '100%', sm: 280 },
                                                                maxWidth: '100%',
                                                                height: 112,
                                                                borderRadius: '16px',
                                                                border: `2px dashed ${coverUrl ? palette.teal : palette.hairline}`,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: coverUrl ? `url(${coverUrl}) center/cover no-repeat` : palette.canvasAlt,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                boxShadow: coverUrl ? shadows.sm : 'none',
                                                                '&:hover': {
                                                                    borderColor: palette.teal,
                                                                    boxShadow: `0 8px 24px ${safeAlpha(palette.teal, 0.15)}`,
                                                                }
                                                            }}
                                                        >
                                                            {!coverUrl && (
                                                                <>
                                                                    <ImageIcon sx={{ color: palette.teal, fontSize: 28, mb: 0.5 }} />
                                                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.65rem', fontWeight: 700 }}>COVER</Typography>
                                                                </>
                                                            )}
                                                            <input ref={coverFileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleCoverUpload} style={{ display: 'none' }} />
                                                        </Box>
                                                    </motion.div>
                                                    {coverUrl && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={handleRemoveCover}
                                                            sx={{ 
                                                                textTransform: 'none', 
                                                                fontWeight: 600, 
                                                                fontSize: '0.75rem',
                                                                color: palette.danger,
                                                                '&:hover': { bgcolor: safeAlpha(palette.danger, 0.08) }
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                    <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, fontWeight: 500 }}>
                                                        Cover (5MB max)
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {uploading && (
                                                <LinearProgress sx={{ mt: 2, borderRadius: 4, height: 4 }} />
                                            )}

                                            <Divider sx={{ my: 2.5, borderColor: palette.hairline }} />
                                            <Box sx={{ p: 1.6, borderRadius: '14px', bgcolor: palette.canvasAlt, border: `1px solid ${palette.hairline}` }}>
                                                <Typography sx={{ fontWeight: 800, color: palette.ink, fontSize: '0.84rem' }}>
                                                    Platform Theme
                                                </Typography>
                                                <Typography sx={{ color: palette.inkLight, fontSize: '0.72rem', mt: .45, lineHeight: 1.55 }}>
                                                    TELCOFAIR colours are managed centrally by Super Admin and automatically apply to both portals. Your company can still manage its own logo and cover image here.
                                                </Typography>
                                            </Box>
                                        </ElevatedCard>

                                        {/* Company Information */}
                                        <ElevatedCard color={palette.rust} delay={0.2} hover={false}>
                                            <SectionHeading 
                                                icon={<BusinessIcon sx={{ fontSize: 19 }} />} 
                                                title="Company Information" 
                                                chip="details"
                                                color={palette.rust}
                                            />
                                            <Grid container spacing={2.5}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Company Name *"
                                                        value={settings.companyName}
                                                        onChange={(e) => handleChange('companyName', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <StorefrontIcon sx={{ color: getReadableColor(palette.rust) }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Company Type *</InputLabel>
                                                        <Select
                                                            value={settings.companyType}
                                                            onChange={(e) => handleChange('companyType', e.target.value)}
                                                            label="Company Type *"
                                                            sx={selectFieldSx}
                                                        >
                                                            {companyTypes.map((type) => (
                                                                <MenuItem key={type} value={type} sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5 }}>{type}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Description"
                                                        value={settings.description}
                                                        onChange={(e) => handleChange('description', e.target.value)}
                                                        multiline
                                                        rows={3}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <DescriptionIcon sx={{ color: palette.purple }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Website"
                                                        value={settings.website}
                                                        onChange={(e) => handleChange('website', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LanguageIcon sx={{ color: getReadableColor(palette.rust) }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Company Size</InputLabel>
                                                        <Select
                                                            value={settings.companySize}
                                                            onChange={(e) => handleChange('companySize', e.target.value)}
                                                            label="Company Size"
                                                            sx={selectFieldSx}
                                                        >
                                                            {companySizes.map((size) => (
                                                                <MenuItem key={size} value={size} sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5 }}>{size}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Year Founded"
                                                        value={settings.yearFounded}
                                                        onChange={(e) => handleChange('yearFounded', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <CalendarIcon sx={{ color: getReadableColor(palette.rust) }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="City"
                                                        value={settings.city}
                                                        onChange={(e) => handleChange('city', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Address"
                                                        value={settings.address}
                                                        onChange={(e) => handleChange('address', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Country *</InputLabel>
                                                        <Select
                                                            value={settings.country?.code || ''}
                                                            onChange={(e) => {
                                                                const country = countries.find(c => c.code === e.target.value);
                                                                handleSelectCountry(country);
                                                            }}
                                                            label="Country *"
                                                            sx={selectFieldSx}
                                                            renderValue={(selected) => {
                                                                const country = countries.find(c => c.code === selected);
                                                                return country ? `${country.flag} ${country.name}` : '';
                                                            }}
                                                        >
                                                            {countries.map((country) => (
                                                                <MenuItem key={country.code} value={country.code} sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5 }}>
                                                                    {country.flag} {country.name} ({country.dialCode})
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Email Address *"
                                                        type="email"
                                                        value={settings.email}
                                                        onChange={(e) => handleChange('email', e.target.value)}
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <EmailIcon sx={{ color: getReadableColor(palette.rust) }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography sx={{ fontWeight: 600, color: palette.inkSoft, fontSize: '0.85rem', mb: 1.5 }}>
                                                        Phone Number *
                                                    </Typography>
                                                    <Grid container spacing={1.5}>
                                                        <Grid size={{ xs: 3 }}>
                                                            <FormControl fullWidth sx={formControlSx}>
                                                                <InputLabel>Code</InputLabel>
                                                                <Select
                                                                    value={settings.dialCode}
                                                                    onChange={(e) => {
                                                                        const country = countries.find(c => c.dialCode === e.target.value);
                                                                        if (country) handleSelectPhoneCountry(country);
                                                                    }}
                                                                    label="Code"
                                                                    sx={selectFieldSx}
                                                                    renderValue={(selected) => {
                                                                        const country = countries.find(c => c.dialCode === selected);
                                                                        return country ? `${country.flag} ${country.dialCode}` : selected;
                                                                    }}
                                                                >
                                                                    {countries.map((country) => (
                                                                        <MenuItem key={country.code} value={country.dialCode} sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5 }}>
                                                                            {country.flag} {country.dialCode}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid size={{ xs: 9 }}>
                                                            <TextField
                                                                fullWidth
                                                                value={settings.phone}
                                                                onChange={handlePhoneChange}
                                                                placeholder="1234567890"
                                                                variant="outlined"
                                                                sx={textFieldSx}
                                                                slotProps={{ input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <PhoneIcon sx={{ color: palette.teal, fontSize: 18 }} />
                                                                        </InputAdornment>
                                                                    ),
                                                                } }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </ElevatedCard>

                                        {/* Services & Regions - WIDENED DROPDOWNS */}
                                         <ElevatedCard color={palette.purple} delay={0.3} hover={false}>
    <SectionHeading 
        icon={<StarsIcon sx={{ fontSize: 19 }} />} 
        title="Services & Regions" 
        chip="capabilities"
        color={palette.purple}
    />
    {/* Added width: '100%' to the Grid container to prevent it from collapsing */}
   <Grid container spacing={2.5} sx={{ width: '100%', flexGrow: 1 }}>
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Primary Service</InputLabel>
            <Select
                fullWidth
                value={settings.primaryService || ''}
                onChange={(e) => handleChange('primaryService', e.target.value)}
                label="Primary Service"
            >
                {primaryServices.map((service) => (
                    <MenuItem key={service} value={service} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {service}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Secondary Service</InputLabel>
            <Select
                fullWidth
                value={settings.secondaryService || ''}
                onChange={(e) => handleChange('secondaryService', e.target.value)}
                label="Secondary Service"
            >
                {secondaryServices.map((service) => (
                    <MenuItem key={service} value={service} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {service}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Primary Region</InputLabel>
            <Select
                fullWidth
                value={settings.primaryRegion || ''}
                onChange={(e) => handleChange('primaryRegion', e.target.value)}
                label="Primary Region"
            >
                {primaryRegions.map((region) => (
                    <MenuItem key={region} value={region} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {region}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Secondary Region</InputLabel>
            <Select
                fullWidth
                value={settings.secondaryRegion || ''}
                onChange={(e) => handleChange('secondaryRegion', e.target.value)}
                label="Secondary Region"
            >
                {secondaryRegions.map((region) => (
                    <MenuItem key={region} value={region} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {region}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Connect With (Primary)</InputLabel>
            <Select
                fullWidth
                value={settings.primaryConnectWith || ''}
                onChange={(e) => handleChange('primaryConnectWith', e.target.value)}
                label="Connect With (Primary)"
            >
                {primaryConnectWith.map((item) => (
                    <MenuItem key={item} value={item} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {item}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
            <InputLabel>Connect With (Secondary)</InputLabel>
            <Select
                fullWidth
                value={settings.secondaryConnectWith || ''}
                onChange={(e) => handleChange('secondaryConnectWith', e.target.value)}
                label="Connect With (Secondary)"
            >
                {secondaryConnectWith.map((item) => (
                    <MenuItem key={item} value={item} sx={{ py: 1.5, px: 2, minHeight: '48px' }}>
                        {item}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
    
    <AnimatePresence>
        {(settings.primaryConnectWith === 'Other' || settings.secondaryConnectWith === 'Other') && (
            <Grid size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label="Other – please specify"
                    value={settings.otherConnectWith || ''}
                    onChange={(e) => handleChange('otherConnectWith', e.target.value)}
                    placeholder="Specify what you're looking to connect with..."
                    variant="outlined"
                    sx={{ minWidth: 200, width: '100%' }}
                    slotProps={{ input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <TrendingUpIcon sx={{ color: palette.warning }} />
                            </InputAdornment>
                        )
                    } }}
                />
            </Grid>
        )}
    </AnimatePresence>
</Grid>
</ElevatedCard>
                                        {/* Social Links */}
                                        <ElevatedCard color={palette.teal} delay={0.4} hover={false}>
                                            <SectionHeading 
                                                icon={<LinkIcon sx={{ fontSize: 19 }} />} 
                                                title="Social Media Links" 
                                                chip="connect"
                                                color={palette.teal}
                                            />
                                            <Grid container spacing={2.5}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="LinkedIn"
                                                        value={settings.linkedin}
                                                        onChange={(e) => handleChange('linkedin', e.target.value)}
                                                        placeholder="https://linkedin.com/company/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LinkedInIcon sx={{ color: '#0a66c2' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Facebook"
                                                        value={settings.facebook}
                                                        onChange={(e) => handleChange('facebook', e.target.value)}
                                                        placeholder="https://facebook.com/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <FacebookIcon sx={{ color: '#1877f2' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Twitter / X"
                                                        value={settings.twitter}
                                                        onChange={(e) => handleChange('twitter', e.target.value)}
                                                        placeholder="https://twitter.com/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <TwitterIcon sx={{ color: '#000' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Instagram"
                                                        value={settings.instagram}
                                                        onChange={(e) => handleChange('instagram', e.target.value)}
                                                        placeholder="https://instagram.com/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <InstagramIcon sx={{ color: '#e4405f' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="YouTube"
                                                        value={settings.youtube}
                                                        onChange={(e) => handleChange('youtube', e.target.value)}
                                                        placeholder="https://youtube.com/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <YouTubeIcon sx={{ color: '#ff0000' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="WhatsApp Business"
                                                        value={settings.whatsapp}
                                                        onChange={(e) => handleChange('whatsapp', e.target.value)}
                                                        placeholder="https://wa.me/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <WhatsAppIcon sx={{ color: '#25d366' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Telegram"
                                                        value={settings.telegram}
                                                        onChange={(e) => handleChange('telegram', e.target.value)}
                                                        placeholder="https://t.me/..."
                                                        variant="outlined"
                                                        sx={textFieldSx}
                                                        slotProps={{ input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <TelegramIcon sx={{ color: '#0088cc' }} />
                                                                </InputAdornment>
                                                            )
                                                        } }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </ElevatedCard>
                                    </Box>
                                </Fade>
                            )}

                            {/* TAB 1: MEETING PREFERENCES - WIDENED DROPDOWNS */}
                           {activeTab === 1 && (
    <Fade in={activeTab === 1} timeout={500}>
        <Box>
            <ElevatedCard color={palette.rust} delay={0.1} hover={false}>
                <SectionHeading 
                    icon={<MeetingIcon sx={{ fontSize: 19 }} />} 
                    title="Meeting Preferences" 
                    chip="settings"
                    color={palette.rust}
                />
                <Grid container spacing={2.5} sx={{ width: '100%', flexGrow: 1 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
                            <InputLabel>Default Meeting Duration</InputLabel>
                            <Select
                                fullWidth
                                value={settings.defaultMeetingDuration || ''}
                                onChange={(e) => handleChange('defaultMeetingDuration', e.target.value)}
                                label="Default Meeting Duration"
                            >
                                {meetingDurations.map((dur) => (
                                    <MenuItem key={dur.value} value={dur.value} sx={{ 
                                        fontSize: '0.95rem', 
                                        fontWeight: 500, 
                                        py: 1.5,
                                        px: 2,
                                        minHeight: '48px',
                                    }}>
                                        {dur.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
                            <InputLabel>Max Participants</InputLabel>
                            <Select
                                fullWidth
                                value={settings.maxParticipants || ''}
                                onChange={(e) => handleChange('maxParticipants', e.target.value)}
                                label="Max Participants"
                            >
                                {[2, 5, 10, 15, 20, 30, 50].map((num) => (
                                    <MenuItem key={num} value={num} sx={{ 
                                        fontSize: '0.95rem', 
                                        fontWeight: 500, 
                                        py: 1.5,
                                        px: 2,
                                        minHeight: '48px',
                                    }}>
                                        {num}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth sx={{ minWidth: 200, width: '100%' }}>
                            <InputLabel>Buffer Time (minutes)</InputLabel>
                            <Select
                                fullWidth
                                value={settings.bufferTime || ''}
                                onChange={(e) => handleChange('bufferTime', e.target.value)}
                                label="Buffer Time"
                            >
                                {[0, 5, 10, 15, 30].map((num) => (
                                    <MenuItem key={num} value={num} sx={{ 
                                        fontSize: '0.95rem', 
                                        fontWeight: 500, 
                                        py: 1.5,
                                        px: 2,
                                        minHeight: '48px',
                                    }}>
                                        {num} min
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography sx={{ color: palette.inkSoft, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                            Allowed Meeting Types
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {meetingTypes.map((type) => (
                                <Chip
                                    key={type.value}
                                    label={type.label}
                                    icon={type.value === 'video' ? <VideoCallIcon /> : type.value === 'in-person' ? <PeopleIcon /> : <PhoneIcon />}
                                    onClick={() => handleMeetingTypeToggle(type.value)}
                                    color={settings.meetingTypes.includes(type.value) ? 'primary' : 'default'}
                                    sx={{ 
                                        cursor: 'pointer',
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        py: 1.5,
                                        px: 1,
                                        '&:hover': { opacity: 0.8 },
                                        '&.MuiChip-colorPrimary': {
                                            bgcolor: `${palette.rust}14`,
                                            color: getReadableColor(palette.rust),
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1, borderColor: palette.hairline }} />
                        <Stack spacing={1}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.autoAcceptMeetings}
                                        onChange={(e) => handleChange('autoAcceptMeetings', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: getReadableColor(palette.pine),
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: palette.pine,
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Auto-accept meeting requests</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.requireMeetingAgenda}
                                        onChange={(e) => handleChange('requireMeetingAgenda', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: getReadableColor(palette.pine),
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: palette.pine,
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Require meeting agenda</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.allowRecording}
                                        onChange={(e) => handleChange('allowRecording', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: getReadableColor(palette.pine),
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: palette.pine,
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Allow meeting recording</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.allowChat}
                                        onChange={(e) => handleChange('allowChat', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: getReadableColor(palette.pine),
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: palette.pine,
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Allow chat during meetings</Typography>}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </ElevatedCard>
        </Box>
    </Fade>
)}

                            {/* TAB 2: CALENDAR SETTINGS */}
                            {activeTab === 2 && (
                                <Fade in={activeTab === 2} timeout={500}>
                                    <Stack spacing={1.5}>
                                        <CalendarIntegrationSettings />
                                        <ElevatedCard color={palette.gold} delay={0.1} hover={false}>
                                            <SectionHeading 
                                                icon={<CalendarIcon sx={{ fontSize: 19 }} />} 
                                                title="Time & Calendar Settings" 
                                                chip="schedule"
                                                color={palette.gold}
                                            />
                                            <Grid container spacing={2.5}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Timezone</InputLabel>
                                                        <Select
                                                            value={settings.timezone}
                                                            onChange={(e) => handleChange('timezone', e.target.value)}
                                                            label="Timezone"
                                                            sx={widenedSelectFieldSx}
                                                        >
                                                            {timezones.map((tz) => (
                                                                <MenuItem key={tz.value} value={tz.value} sx={{ 
                                                                    fontSize: '0.95rem', 
                                                                    fontWeight: 500, 
                                                                    py: 1.5,
                                                                    px: 2,
                                                                    minHeight: '48px',
                                                                }}>
                                                                    {tz.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Date Format</InputLabel>
                                                        <Select
                                                            value={settings.dateFormat}
                                                            onChange={(e) => handleChange('dateFormat', e.target.value)}
                                                            label="Date Format"
                                                            sx={widenedSelectFieldSx}
                                                        >
                                                            {dateFormats.map((df) => (
                                                                <MenuItem key={df.value} value={df.value} sx={{ 
                                                                    fontSize: '0.95rem', 
                                                                    fontWeight: 500, 
                                                                    py: 1.5,
                                                                    px: 2,
                                                                    minHeight: '48px',
                                                                }}>
                                                                    {df.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControl fullWidth sx={formControlSx}>
                                                        <InputLabel>Language</InputLabel>
                                                        <Select
                                                            value={settings.language}
                                                            onChange={(e) => handleChange('language', e.target.value)}
                                                            label="Language"
                                                            sx={widenedSelectFieldSx}
                                                        >
                                                            {languages.map((lang) => (
                                                                <MenuItem key={lang.value} value={lang.value} sx={{ 
                                                                    fontSize: '0.95rem', 
                                                                    fontWeight: 500, 
                                                                    py: 1.5,
                                                                    px: 2,
                                                                    minHeight: '48px',
                                                                }}>
                                                                    {lang.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.autoSyncCalendar}
                                                                onChange={(e) => handleChange('autoSyncCalendar', e.target.checked)}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                        color: getReadableColor(palette.pine),
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                        backgroundColor: palette.pine,
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Auto-sync with calendar</Typography>}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography sx={{ color: palette.inkSoft, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                                                        Working Hours
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                        <TextField
                                                            label="Start"
                                                            type="time"
                                                            value={settings.workingHours.start}
                                                            onChange={(e) => handleNestedChange('workingHours', 'start', e.target.value)}
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            sx={{ ...textFieldSx, width: 150 }}
                                                        />
                                                        <TextField
                                                            label="End"
                                                            type="time"
                                                            value={settings.workingHours.end}
                                                            onChange={(e) => handleNestedChange('workingHours', 'end', e.target.value)}
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            sx={{ ...textFieldSx, width: 150 }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography sx={{ color: palette.inkSoft, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                                                        Working Days
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                                            <Chip
                                                                key={day}
                                                                label={day}
                                                                onClick={() => handleWorkingDaysToggle(day)}
                                                                color={settings.workingHours.days.includes(day) ? 'primary' : 'default'}
                                                                sx={{ 
                                                                    cursor: 'pointer',
                                                                    borderRadius: '10px',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.75rem',
                                                                    '&:hover': { opacity: 0.8 },
                                                                    '&.MuiChip-colorPrimary': {
                                                                        bgcolor: `${palette.rust}14`,
                                                                        color: getReadableColor(palette.rust),
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </ElevatedCard>
                                    </Stack>
                                </Fade>
                            )}

                            {/* TAB 3: NOTIFICATIONS */}
                            {activeTab === 3 && (
                                <Fade in={activeTab === 3} timeout={500}>
                                    <Box>
                                        <ElevatedCard color={palette.purple} delay={0.1} hover={false}>
                                            <SectionHeading 
                                                icon={<NotificationsIcon sx={{ fontSize: 19 }} />} 
                                                title="Notification Preferences" 
                                                chip="alerts"
                                                color={palette.purple}
                                            />
                                            <List sx={{ p: 0 }}>
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><EmailIcon sx={{ color: getReadableColor(palette.rust) }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>Email Notifications</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Receive notifications via email</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.emailNotifications} 
                                                            onChange={(e) => handleChange('emailNotifications', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider sx={{ borderColor: palette.hairline }} />
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><NotificationsIcon sx={{ color: palette.teal }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>Push Notifications</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Receive push notifications in browser</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.pushNotifications} 
                                                            onChange={(e) => handleChange('pushNotifications', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider sx={{ borderColor: palette.hairline }} />
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><PhoneIcon sx={{ color: palette.purple }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>SMS Notifications</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Receive notifications via SMS</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.smsNotifications} 
                                                            onChange={(e) => handleChange('smsNotifications', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider sx={{ borderColor: palette.hairline }} />
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><CalendarIcon sx={{ color: getReadableColor(palette.gold) }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>Meeting Reminders</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Get reminders before meetings</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.meetingReminders} 
                                                            onChange={(e) => handleChange('meetingReminders', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider sx={{ borderColor: palette.hairline }} />
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><CheckCircleIcon sx={{ color: getReadableColor(palette.pine) }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>Meeting Confirmations</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Receive confirmation when meetings are scheduled</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.meetingConfirmations} 
                                                            onChange={(e) => handleChange('meetingConfirmations', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider sx={{ borderColor: palette.hairline }} />
                                                <ListItem sx={{ borderRadius: '12px', '&:hover': { bgcolor: safeAlpha(palette.rust, 0.04) } }}>
                                                    <ListItemIcon><CancelIcon sx={{ color: palette.danger }} /></ListItemIcon>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={<Typography sx={{ fontWeight: 600, color: palette.ink }}>Meeting Cancellations</Typography>}
                                                        secondary={<Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>Receive notifications when meetings are cancelled</Typography>}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Switch 
                                                            checked={settings.meetingCancellations} 
                                                            onChange={(e) => handleChange('meetingCancellations', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            </List>
                                        </ElevatedCard>
                                    </Box>
                                </Fade>
                            )}

                            {/* TAB 4: SECURITY */}
                            {activeTab === 4 && (
                                <Fade in={activeTab === 4} timeout={500}>
                                    <Box>
                                        <ElevatedCard color={palette.danger} delay={0.1} hover={false}>
                                            <SectionHeading 
                                                icon={<SecurityIcon sx={{ fontSize: 19 }} />} 
                                                title="Security Settings" 
                                                chip="protection"
                                                color={palette.danger}
                                            />
                                            <Alert severity="info" sx={{ mb: 2, borderRadius: '14px' }}>
                                                Authentication MFA/session enforcement and meeting-password/screen-share policy require secured Firebase/backend enforcement. These switches are intentionally read-only here so the portal never claims protection that the browser cannot guarantee.
                                            </Alert>
                                            <Stack spacing={2}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch 
                                                            checked={settings.twoFactorAuth} 
                                                            disabled
                                                            onChange={(e) => handleChange('twoFactorAuth', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Two-Factor Authentication</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch 
                                                            checked={settings.requireMeetingPassword} 
                                                            disabled
                                                            onChange={(e) => handleChange('requireMeetingPassword', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Require password for meetings</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch 
                                                            checked={settings.allowScreenSharing} 
                                                            disabled
                                                            onChange={(e) => handleChange('allowScreenSharing', e.target.checked)} 
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: getReadableColor(palette.pine),
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: palette.pine,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Allow screen sharing</Typography>}
                                                />
                                                <Box>
                                                    <Typography sx={{ color: palette.inkSoft, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                                                        Session Timeout (minutes)
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Slider 
                                                            value={settings.sessionTimeout} 
                                                            disabled
                                                            onChange={(e, val) => handleChange('sessionTimeout', val)} 
                                                            min={15} 
                                                            max={240} 
                                                            step={15} 
                                                            marks 
                                                            valueLabelDisplay="auto" 
                                                            sx={{ 
                                                                flex: 1,
                                                                color: getReadableColor(palette.rust),
                                                                '& .MuiSlider-track': { backgroundColor: palette.rust },
                                                                '& .MuiSlider-thumb': { backgroundColor: palette.rust },
                                                            }} 
                                                        />
                                                        <Typography sx={{ minWidth: 60, fontWeight: 700, color: palette.ink }}>{settings.sessionTimeout}m</Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        </ElevatedCard>
                                    </Box>
                                </Fade>
                            )}

                            {/* TAB 5: ADVANCED SETTINGS */}
                            {activeTab === 5 && (
                                <Fade in={activeTab === 5} timeout={500}>
                                    <Box>
                                        <ElevatedCard color={palette.teal} delay={0.1} hover={false}>
                                            <SectionHeading 
                                                icon={<DataIcon sx={{ fontSize: 19 }} />} 
                                                title="Advanced Settings" 
                                                chip="expert"
                                                color={palette.teal}
                                            />
                                            <Grid container spacing={2.5}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth 
                                                        label="Email Domain" 
                                                        value={settings.emailDomain}
                                                        onChange={(e) => handleChange('emailDomain', e.target.value)}
                                                        variant="outlined" 
                                                        sx={textFieldSx} 
                                                        placeholder="example.com"
                                                        helperText={<Typography component="span" sx={{ color: palette.inkLight, fontSize: '0.7rem' }}>Email domain for your company</Typography>}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth 
                                                        label="Meeting Room Prefix" 
                                                        value={settings.meetingRoomPrefix}
                                                        onChange={(e) => handleChange('meetingRoomPrefix', e.target.value)}
                                                        variant="outlined" 
                                                        sx={textFieldSx} 
                                                        placeholder="room-"
                                                        helperText={<Typography component="span" sx={{ color: palette.inkLight, fontSize: '0.7rem' }}>Prefix for meeting room IDs</Typography>}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Divider sx={{ my: 1, borderColor: palette.hairline }} />
                                                    <Stack spacing={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch 
                                                                    checked={settings.allowPublicProfile} 
                                                                    onChange={(e) => handleChange('allowPublicProfile', e.target.checked)} 
                                                                    sx={{
                                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                                            color: getReadableColor(palette.pine),
                                                                        },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                            backgroundColor: palette.pine,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Allow public profile visibility</Typography>}
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Switch 
                                                                    checked={settings.showInSearch} 
                                                                    onChange={(e) => handleChange('showInSearch', e.target.checked)} 
                                                                    sx={{
                                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                                            color: getReadableColor(palette.pine),
                                                                        },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                            backgroundColor: palette.pine,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Show company in search results</Typography>}
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Switch 
                                                                    checked={settings.enableAnalytics} 
                                                                    onChange={(e) => handleChange('enableAnalytics', e.target.checked)} 
                                                                    sx={{
                                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                                            color: getReadableColor(palette.pine),
                                                                        },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                            backgroundColor: palette.pine,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography sx={{ fontWeight: 500, color: palette.ink }}>Enable analytics tracking</Typography>}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Alert 
                                                        severity="info" 
                                                        sx={{ 
                                                            mt: 1, 
                                                            borderRadius: '12px',
                                                            bgcolor: `${palette.info}08`,
                                                            border: `1px solid ${palette.info}20`,
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: palette.inkSoft }}>
                                                            <strong>Advanced settings</strong> - Changes here affect how your company appears and operates on the platform.
                                                        </Typography>
                                                    </Alert>
                                                </Grid>
                                            </Grid>
                                        </ElevatedCard>
                                    </Box>
                                </Fade>
                            )}

                            {/* TAB 6: COMPANY SUBSCRIPTION & USER SEATS */}
                            {activeTab === 6 && (
                                <Fade in={activeTab === 6} timeout={500}>
                                    <Stack spacing={1.2}>
                                        <SubscriptionSettings companyId={companyId} />
                                    </Stack>
                                </Fade>
                            )}


                        </Box>

                        {/* Footer */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            style={{ marginTop: 24, display: activeTab >= 6 ? 'none' : 'block' }}
                        >
                            <Divider sx={{ mb: 2.5, borderColor: palette.hairline }} />
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                flexWrap: 'wrap', 
                                gap: 2,
                                bgcolor: palette.canvasAlt,
                                p: 2,
                                borderRadius: '16px',
                                border: `1px solid ${palette.hairline}`,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Chip 
                                        icon={<CheckCircleIcon />} 
                                        label="Save Changes applies company settings" 
                                        size="small"
                                        sx={{ 
                                            bgcolor: 'rgba(45,90,71,0.08)', 
                                            color: getReadableColor(palette.pine),
                                            fontWeight: 600,
                                        }}
                                    />
                                    <Chip
                                        icon={<LiveIcon sx={{ fontSize: 10, color: `${palette.pine} !important` }} />}
                                        label="Secure settings workspace"
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(45,90,71,0.05)',
                                            color: getReadableColor(palette.pine),
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                        }}
                                    />
                                </Box>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        onClick={handleSave}
                                        disabled={saving}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            py: 0.8,
                                            px: 3,
                                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: '0 6px 16px -8px rgba(199,91,46,0.45)',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px -8px rgba(199,91,46,0.55)',
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:disabled': {
                                                background: palette.inkLight,
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        {saving ? 'Saving...' : 'Save All Settings'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </motion.div>
                    </ElevatedCard>
                </motion.div>
                </Box>
            </Container>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes shimmerRule {
                        0%, 100% { opacity: 0.7; width: 40px; }
                        50% { opacity: 1; width: 56px; }
                    }
                    @keyframes shimmerEdge {
                        0% { background-position: 0% 0%; }
                        100% { background-position: 300% 0%; }
                    }
                    @keyframes auroraDrift {
                        0% { background-position: 0% 0%; }
                        50% { background-position: 100% 60%; }
                        100% { background-position: 0% 0%; }
                    }
                    @keyframes grainDrift {
                        0% { transform: translate(0, 0); }
                        20% { transform: translate(-2%, 2%); }
                        40% { transform: translate(2%, -1%); }
                        60% { transform: translate(-1%, -2%); }
                        80% { transform: translate(1%, 1%); }
                        100% { transform: translate(0, 0); }
                    }
                    @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                    * {
                        scrollbar-width: thin;
                        scrollbar-color: ${palette.hairline} transparent;
                    }
                    ::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    ::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: ${palette.hairline};
                        borderRadius: 3px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: ${palette.hairlineStrong};
                    }
                `}
            </style>
        </Box>
    );
};

export default CompanySettings;